import type { LocationSuggestion } from '../../../shared/services/locationService/locationService';

import type { MkLocationSuggestion } from '@seek/ca-graphql-schema/types';
import {
  buildCommunityGuidelinesURI,
  buildPrivacyURI,
  useConfig,
  useValidation,
  type Rules,
} from '@seek/libs-shared';
import { Box, Heading, Stack, Text, TextLink } from 'braid-design-system';
import { useEffect, useState } from 'react';
import { DEFAULT_VARIANT_SCHEMA_VERSION } from '../../../shared/constants/launchDarklyFeatureFlags';
import type {
  ConfirmedRoles,
  RoleTitle,
} from '../../../shared/services/roleService/roleService';
import {
  CURRENT_PAGE_YOUR_ROLE,
  trackClickYourRoleSubSection,
  trackDisplayPage,
  trackValidationErrorYourRolePage,
  trackWriteAReviewSubmitted,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import { ReviewPreview } from '../../components/ReviewPreview/ReviewPreview';
import {
  JobTitleQuestion,
  type JobTitleValue,
} from '../../questions/AutoSuggestionQuestions/JobTitleQuestions/JobTitleQuestion';
import { LocationQuestion } from '../../questions/AutoSuggestionQuestions/LocationQuestion/LocationQuestion';
import { FinishQuestion } from '../../questions/GroupedQuestions/WorkHistoryQuestion/FinishQuestion';
import { StartQuestion } from '../../questions/GroupedQuestions/WorkHistoryQuestion/StartQuestion';
import { GenericTitleQuestion } from '../../questions/SimpleQuestions/GenericTitleQuestion';
import { WorkTypeQuestion } from '../../questions/SimpleQuestions/WorkTypeQuestion';
import {
  type MonthPickerValue,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { mapErrorMessage } from '../../utils/mapErrorMessage';
import { validationRules } from '../../validation/validationRules';
import { testWorkHistoryRequired } from '../../validation/validator/workHistoryRequired';

type Props = PageProps & {
  getRoleTitles: (query: string) => Promise<RoleTitle[]>;
  isSubmitting: boolean;
  onSearchLocations: (
    query: string,
  ) => Promise<Partial<MkLocationSuggestion>[]>;
  onUpdateRole: (role: ConfirmedRoles) => Promise<void>;
};

export const YourRolePage = ({
  review,
  reviewFormContext,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
  onSearchLocations,
  onUpdateRole,
  getRoleTitles,
}: Props) => {
  const config = useConfig();
  const prefilled = Boolean(review.isWorkHistoryPrefilled);

  const [isJobTitleUpdating, setIsJobTitleUpdating] = useState(false);
  const [isLocationUpdating, setIsLocationUpdating] = useState(false);

  useEffect(() => {
    if (reviewFormContext?.variant) {
      trackDisplayPage({
        config,
        context: reviewFormContext,
        currentPage: CURRENT_PAGE_YOUR_ROLE,
        variant: reviewFormContext.variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormContext?.variant]);

  const fields = {
    workType: review?.workType,
    jobTitleText: review?.jobTitleText,
    fullLocation: review?.fullLocation,
    workHistoryFrom: review?.workHistoryFrom,
    workHistoryTo: review?.workHistoryTo,
    workHistory: {
      from: review?.workHistoryFrom,
      to: review?.workHistoryTo,
    },
    genericTitle: review?.genericTitle,
    isGenericTitle: review?.isGenericTitle,
  };

  const {
    workType,
    jobTitleText,
    fullLocation,
    workHistoryFrom,
    workHistoryTo,
    workHistory,
    genericTitle,
    isGenericTitle,
  } = validationRules(config.language);

  const rules = {
    workType,
    jobTitleText,
    fullLocation,
    workHistoryFrom,
    ...(!review.isStillWorkHere
      ? {
          workHistoryTo,
          workHistory,
        }
      : {}),
    genericTitle,
    isGenericTitle,
  } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validate,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid, errors } = validateWithErrors();

    if (isValid && onSubmit) {
      onSubmit();

      trackWriteAReviewSubmitted({
        config,
        context: reviewFormContext,
        variant: reviewFormContext.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
      });
    } else {
      trackValidationErrorYourRolePage({
        config,
        context: reviewFormContext,
        errorMessage: errors.map((error) => mapErrorMessage(error.fieldName)),
        errorText: errors.map((error) => error.message),
        prefilled,
      });
    }

    void onUpdateRole({
      id: review.roleId,
      ...(review.companyName
        ? {
            company: {
              text: review.companyName,
            },
          }
        : {}),
      title: {
        text: review.jobTitleText ?? '',
      },
      from: review.workHistoryFrom as MonthPickerValue,
      to: review.workHistoryTo as MonthPickerValue,
    });
    if (!isValid) {
      return;
    }
  };

  const handleJobTitleQuestionChange = (value: JobTitleValue) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'jobTitleText',
      previousValue: review,
    })(value.text);

    setter((prev) => ({
      ...prev,
      jobTitleId: value.id,
    }));
  };

  const handleGenericTitleQuestionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'genericTitle',
      previousValue: review,
    })(value);

    setter((prev) => ({
      ...prev,
      genericTitle: value,
    }));
  };

  const handleIsGenericTitleCheckBoxChange = () => {
    setter({
      ...review,
      isGenericTitle: !review.isGenericTitle,
      genericTitle: review.isGenericTitle ? '' : review.genericTitle,
    });
  };

  const handleLocationChange = (value: LocationSuggestion) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'fullLocation',
      previousValue: review,
    })(value.text);

    setter((prev) => ({
      ...prev,
      locationId: value.id,
    }));
  };

  const handleWorkTypeChange = (value: string) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'workType',
      previousValue: review,
    })(value);

    trackClickYourRoleSubSection({
      currentPageSubsection: 'work_type',
      config,
      context: reviewFormContext,
      prefilled,
    });
  };

  const handleStartQuestionChange = (value: MonthPickerValue) => {
    setter({
      ...review,
      workHistoryFrom: value,
      workHistory: {
        ...review.workHistory,
        from: value,
      },
    });

    const shouldTestWorkHistory =
      testWorkHistoryRequired(value) &&
      review?.workHistoryTo &&
      testWorkHistoryRequired(review?.workHistoryTo);

    if (!review.isStillWorkHere) {
      validate({
        workHistoryFrom: value,
        ...(shouldTestWorkHistory
          ? {
              workHistory: {
                from: value,
                to: review?.workHistoryTo,
              },
            }
          : {}),
      });
    }

    trackClickYourRoleSubSection({
      currentPageSubsection: 'start_date',
      config,
      context: reviewFormContext,
      prefilled,
    });
  };

  const handleFinishQuestionChange = (value: MonthPickerValue) => {
    setter({
      ...review,
      workHistoryTo: value,
      workHistory: {
        ...review.workHistory,
        to: value,
      },
    });

    const shouldTestWorkHistory =
      testWorkHistoryRequired(value) &&
      review?.workHistoryFrom &&
      testWorkHistoryRequired(review?.workHistoryFrom);

    if (!review.isStillWorkHere) {
      validate({
        workHistoryTo: value,
        ...(shouldTestWorkHistory
          ? {
              workHistory: {
                from: review?.workHistoryFrom,
                to: value,
              },
            }
          : {}),
      });
    }

    trackClickYourRoleSubSection({
      currentPageSubsection: 'end_date',
      config,
      context: reviewFormContext,
      prefilled,
    });
  };

  const handleIsStillWorkHereChange = () => {
    setter({ ...review, isStillWorkHere: !review.isStillWorkHere });
    trackClickYourRoleSubSection({
      currentPageSubsection: 'i_still_work_here',
      config,
      context: reviewFormContext,
      prefilled,
    });
  };

  return (
    <ReviewPageLayoutNoStepper
      hideStepper={true}
      isContinueLoading={isSubmitting}
      onContinue={handleOnContinue}
      progress={pageNumber}
    >
      <Stack space="large">
        <Heading level="1">Your role</Heading>
        <Text size="standard" tone="secondary">
          We'll save this information to your SEEK profile.
        </Text>

        <Stack space="small">
          <Stack space="none">
            <JobTitleQuestion
              onChange={handleJobTitleQuestionChange}
              value={{ text: review?.jobTitleText, id: review?.jobTitleId }}
              description={'This will be added to your SEEK profile'}
              tone={getTone('jobTitleText')}
              message={getMessage('jobTitleText')}
              getRoleTitles={getRoleTitles}
              onValueSelected={() => {
                trackClickYourRoleSubSection({
                  currentPageSubsection: 'job_title',
                  config,
                  context: reviewFormContext,
                  prefilled,
                });
              }}
              setIsJobTitleUpdating={setIsJobTitleUpdating}
            />
            <Box
              paddingBottom={
                getTone('jobTitleText') === 'critical' ? 'medium' : 'none'
              }
            />
            <GenericTitleQuestion
              isGenericTitle={review?.isGenericTitle || false}
              onCheckedChange={handleIsGenericTitleCheckBoxChange}
              onTextChange={handleGenericTitleQuestionChange}
              genericTitleText={review?.genericTitle || ''}
              tone={getTone('genericTitle')}
              message={getMessage('genericTitle')}
            />
          </Stack>
          <LocationQuestion
            onChange={handleLocationChange}
            searchLocations={onSearchLocations}
            value={{
              text: review?.fullLocation,
              id: review?.locationId,
            }}
            zone={config.zone}
            tone={getTone('fullLocation')}
            message={getMessage('fullLocation')}
            onValueSelected={() => {
              trackClickYourRoleSubSection({
                currentPageSubsection: 'location_selected',
                config,
                context: reviewFormContext,
                prefilled,
              });
            }}
            setIsLocationUpdating={setIsLocationUpdating}
          />
          <WorkTypeQuestion
            onChange={handleWorkTypeChange}
            value={review?.workType}
            tone={getTone('workType')}
            message={getMessage('workType')}
          />
          <StartQuestion
            value={review.workHistoryFrom}
            onChange={handleStartQuestionChange}
            tone={getTone('workHistoryFrom')}
            message={getMessage('workHistoryFrom')}
          />
          <FinishQuestion
            value={review.workHistoryTo}
            onChange={handleFinishQuestionChange}
            isStillWorkHere={Boolean(review.isStillWorkHere)}
            onIsStillWorkHereChange={handleIsStillWorkHereChange}
            tone={
              getTone('workHistoryTo') === 'critical'
                ? getTone('workHistoryTo')
                : getTone('workHistory')
            }
            message={getMessage('workHistoryTo') || getMessage('workHistory')}
          />
          <ReviewPreview
            rating={Number(review?.overallRating)}
            jobTitle={review?.jobTitleText}
            genericTitle={review?.genericTitle}
            location={review?.fullLocation}
            workHistoryFrom={review?.workHistoryFrom}
            workHistoryTo={review?.workHistoryTo}
            isJobTitleUpdating={isJobTitleUpdating}
            isLocationUpdating={isLocationUpdating}
            isStillWorkHere={review?.isStillWorkHere}
          />
          <Box paddingY="medium">
            <Text size="small">
              Personal information submitted will follow our{' '}
              <TextLink
                href={buildPrivacyURI({
                  country: config.country,
                  environment: config.environment,
                  language: config.language,
                })}
                target="_blank"
                weight="weak"
              >
                Privacy Statement
              </TextLink>
              . By submitting, you agree to our{' '}
              <TextLink
                href={buildCommunityGuidelinesURI({
                  locale: config.locale,
                  zone: config.zone,
                  experience: config.experience,
                })}
                target="_blank"
                weight="weak"
              >
                Reviews Guidelines
              </TextLink>
              .
            </Text>
          </Box>
        </Stack>
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
